/**
 * RecipeBuddy – Frankie the Recipe Buddy (Gemini Live API)
 *
 * Flow:
 *  1. User opens panel  → request mic permission
 *  2. Mic granted       → connect WebSocket → Frankie greets by voice
 *  3. On "ready"        → mic starts automatically (user can speak anytime)
 *  4. User taps mic     → stop capturing (end of turn) → Frankie replies
 *  5. When Frankie finishes speaking → mic starts again (repeat)
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import '../styles/recipe-buddy.css';

const VOICE = 'Algenib';

// Pre-buffer before starting playback so we don't play tiny first chunks (≈150ms at 24kHz)
const PREBUFFER_SAMPLES = 3600;
// One big merge per buffer = fewer boundaries, less gargling (≈350ms at 24kHz)
const MIN_SAMPLES_PER_PLAY = 8400;

// Derive WebSocket URL. In prod, use REACT_APP_WS_BASE_URL if your API runs elsewhere
// (e.g. Railway, Render). Vercel does NOT support WebSockets — backend must run on a
// platform that does (Railway, Render, Fly.io, VPS with Node).
function getWsUrl(recipeId, language = 'en', isGuest = false) {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  let host;
  const wsBase = process.env.REACT_APP_WS_BASE_URL;
  if (wsBase) {
    host = wsBase.replace(/^https?:\/\//, '').replace(/^wss?:\/\//, '').replace(/\/$/, '');
  } else if (process.env.NODE_ENV === 'production') {
    host = window.location.host;
  } else {
    host = `${window.location.hostname}:5010`;
  }
  const params = new URLSearchParams({ recipeId, voice: VOICE, language });
  if (isGuest) params.set('isGuest', '1');
  return `${proto}://${host}/ws/recipe-buddy?${params.toString()}`;
}

// Decode base64 string → Float32Array of PCM samples (16-bit LE → float).
// Trims to even byte length so we never have a partial sample (avoids pops at chunk boundaries).
function base64ToFloat32(base64) {
  const binary = atob(base64);
  const len = binary.length;
  const evenLen = len - (len % 2); // only use complete 16-bit samples
  if (evenLen === 0) return new Float32Array(0);
  const bytes = new Uint8Array(evenLen);
  for (let i = 0; i < evenLen; i++) bytes[i] = binary.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768;
  return float32;
}

// Encode Float32Array of mic samples → base64 16-bit LE PCM
function float32ToBase64(float32) {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = s < 0 ? s * 32768 : s * 32767;
  }
  const bytes = new Uint8Array(int16.buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

// Play a loud sizzle sound (1–2 sec) when onion lands — Web Audio filtered noise
function playSizzle() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const duration = 1.5;
    const sampleRate = ctx.sampleRate;
    const numSamples = Math.floor(duration * sampleRate);
    const buffer = ctx.createBuffer(1, numSamples, sampleRate);
    const data = buffer.getChannelData(0);
    let last = 0;
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const decay = Math.exp(-t * 3);
      last = 0.97 * last + 0.03 * (Math.random() * 2 - 1);
      data[i] = last * decay * 0.55;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2400;
    filter.Q.value = 1.5;
    source.connect(filter);
    filter.connect(ctx.destination);
    source.start(0);
  } catch (_) {}
}

const GUEST_TURN_LIMIT = 2;
const GUEST_LIMIT_STORAGE_KEY = 'frankie_guest_limit_reached';

export default function RecipeBuddy({ recipe, recipeId, isAuthenticated = true, onSignInRequired, initialOpen = false }) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  // Language chosen before connecting: null = not yet chosen, 'en' | 'af'
  const [language, setLanguage] = useState(null);
  // status: 'idle' | 'connecting' | 'ready' | 'listening' | 'waiting_for_reply' | 'speaking' | 'paused' | 'error'
  const [status, setStatus] = useState('idle');
  const isPausedRef = useRef(false);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown'|'prompting'|'granted'|'denied'
  const [lastUserText, setLastUserText] = useState('');
  const [lastBuddyText, setLastBuddyText] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const hasReceivedFirstAudioRef = useRef(false);
  // Guest turn count: when not authenticated, after 2 user turns show sign-in and block on all recipes
  const guestTurnCountRef = useRef(0);
  const [guestLimitReached, setGuestLimitReached] = useState(false);
  const guestLimitReachedRef = useRef(false);
  const signInRequiredFiredRef = useRef(false);

  const wsRef = useRef(null);
  const userSpeakingRef = useRef(false);
  const audioCtxRef = useRef(null);
  const micStreamRef = useRef(null);
  const scriptNodeRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const isListeningRef = useRef(false);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);
  const startMicCaptureRef = useRef(() => {});
  const wakeLockRef = useRef(null);

  // ─── Screen wake lock (keep phone screen on during conversation) ───────────
  const requestWakeLock = useCallback(async () => {
    if (!navigator.wakeLock) return;
    try {
      if (wakeLockRef.current) return;
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      wakeLockRef.current.addEventListener('release', () => {
        wakeLockRef.current = null;
      });
    } catch (e) {
      console.warn('[RecipeBuddy] Wake lock request failed:', e.message);
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      try {
        wakeLockRef.current.release();
      } catch (_) {}
      wakeLockRef.current = null;
    }
  }, []);

  // ─── Audio playback ───────────────────────────────────────────────────────

  const ensureAudioContext = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const playNextChunk = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      setStatus((s) => (s === 'speaking' ? 'ready' : s));
      // Restart mic so user can speak again — unless paused or guest limit reached
      setTimeout(() => {
        if (!isPausedRef.current && !guestLimitReachedRef.current) startMicCaptureRef.current();
      }, 100);
      return;
    }
    isPlayingRef.current = true;
    setStatus('speaking');

    const ctx = ensureAudioContext();
    // Merge many chunks into one large buffer to avoid chunk-boundary glitches
    const merged = [];
    while (audioQueueRef.current.length > 0 && merged.length < MIN_SAMPLES_PER_PLAY) {
      merged.push(...audioQueueRef.current.shift());
    }
    const totalLen = merged.length;
    if (totalLen === 0) {
      playNextChunk();
      return;
    }
    const samples = new Float32Array(merged);

    const buffer = ctx.createBuffer(1, samples.length, 24000);
    buffer.copyToChannel(samples, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    const now = ctx.currentTime;
    const startTime = Math.max(now, nextPlayTimeRef.current);
    source.start(startTime);
    nextPlayTimeRef.current = startTime + buffer.duration;
    sourceNodeRef.current = source;

    source.onended = playNextChunk;
  }, [ensureAudioContext]);

  const enqueueAudio = useCallback((base64pcm) => {
    const samples = base64ToFloat32(base64pcm);
    audioQueueRef.current.push(samples);
    if (!isPlayingRef.current) {
      const totalQueued = audioQueueRef.current.reduce((n, arr) => n + arr.length, 0);
      if (totalQueued >= PREBUFFER_SAMPLES) {
        if (!hasReceivedFirstAudioRef.current) {
          hasReceivedFirstAudioRef.current = true;
          setIntroDone(true);
        }
        playNextChunk();
      }
    }
  }, [playNextChunk]);

  const stopPlayback = useCallback(() => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
    if (sourceNodeRef.current) {
      try { sourceNodeRef.current.stop(); } catch (_) {}
      sourceNodeRef.current = null;
    }
  }, []);

  // ─── Mic capture ─────────────────────────────────────────────────────────

  const startMicCapture = useCallback(() => {
    if (!micStreamRef.current || !wsRef.current) return;
    if (isListeningRef.current) return;
    if (guestLimitReachedRef.current) return;
    isListeningRef.current = true;
    setStatus('listening');
    stopPlayback(); // interrupt Gemini if speaking

    const ctx = ensureAudioContext();
    const source = ctx.createMediaStreamSource(micStreamRef.current);

    // ScriptProcessorNode: capture mic at AudioContext sample rate, downsample to 16kHz
    const bufferSize = 4096;
    const scriptNode = ctx.createScriptProcessor(bufferSize, 1, 1);
    const inputSampleRate = ctx.sampleRate;
    const targetSampleRate = 16000;
    const ratio = inputSampleRate / targetSampleRate;

    scriptNode.onaudioprocess = (event) => {
      if (!isListeningRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
      if (guestLimitReachedRef.current) return; // Don't send audio after guest limit
      const inputData = event.inputBuffer.getChannelData(0);
      // Voice activity: RMS above threshold = user is speaking (for mic vibrate feedback)
      let sum = 0;
      for (let i = 0; i < inputData.length; i++) sum += inputData[i] * inputData[i];
      const rms = Math.sqrt(sum / inputData.length);
      userSpeakingRef.current = rms > 0.02;
      // Downsample
      const outputLength = Math.floor(inputData.length / ratio);
      const downsampled = new Float32Array(outputLength);
      for (let i = 0; i < outputLength; i++) {
        downsampled[i] = inputData[Math.floor(i * ratio)];
      }
      const b64 = float32ToBase64(downsampled);
      wsRef.current.send(JSON.stringify({ type: 'audio', data: b64 }));
    };

    source.connect(scriptNode);
    scriptNode.connect(ctx.destination);
    scriptNodeRef.current = scriptNode;
  }, [ensureAudioContext, stopPlayback]);

  const stopMicCapture = useCallback(() => {
    isListeningRef.current = false;
    userSpeakingRef.current = false;
    setIsUserSpeaking(false);
    if (scriptNodeRef.current) {
      try { scriptNodeRef.current.disconnect(); } catch (_) {}
      scriptNodeRef.current = null;
    }
    // Caller sets status (e.g. 'waiting_for_reply' when user taps stop)
  }, []);

  // Throttled sync of voice activity to state (so mic can show vibrate when user speaks)
  useEffect(() => {
    if (status !== 'listening') return;
    const interval = setInterval(() => {
      setIsUserSpeaking(userSpeakingRef.current);
    }, 80);
    return () => clearInterval(interval);
  }, [status]);

  // ─── WebSocket connection ─────────────────────────────────────────────────

  const connectWs = useCallback((lang) => {
    if (wsRef.current) return;
    setStatus('connecting');
    setErrorMsg('');

    const ws = new WebSocket(getWsUrl(recipeId, lang, !isAuthenticated));
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('[RecipeBuddy] WS connected');
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'limit_reached') {
          guestLimitReachedRef.current = true;
          setGuestLimitReached(true);
          if (onSignInRequired) onSignInRequired();
        } else if (msg.type === 'ready') {
          setStatus('ready');
          setLastBuddyText('');
          setLastUserText('');
          playSizzle();
          // Auto-start mic so user can speak immediately (tap to stop when done) — unless guest limit reached
          setTimeout(() => {
            if (!guestLimitReachedRef.current) startMicCaptureRef.current();
          }, 300);
        } else if (msg.type === 'audio') {
          enqueueAudio(msg.data);
        } else if (msg.type === 'text') {
          setLastBuddyText(msg.data);
        } else if (msg.type === 'error') {
          setErrorMsg(msg.message || 'An error occurred.');
          setStatus('error');
          setIntroDone(true);
        }
      } catch (e) {
        console.error('[RecipeBuddy] WS message parse error:', e);
      }
    };

    ws.onerror = (e) => {
      console.error('[RecipeBuddy] WS error:', e);
      setErrorMsg('Frankie couldn\'t connect. If the site is on Vercel, set REACT_APP_WS_BASE_URL to your EC2 backend URL (e.g. https://api.whattomake.co.za) and rebuild.');
      setStatus('error');
    };

    ws.onclose = (e) => {
      console.log('[RecipeBuddy] WS closed', { code: e.code, reason: e.reason, wasClean: e.wasClean });
      wsRef.current = null;
      // Use functional update to avoid stale closure on `status`
      setStatus((s) => (s !== 'error' ? 'idle' : s));
    };
  }, [recipeId, isAuthenticated, enqueueAudio, onSignInRequired]);

  // Keep ref updated so playNextChunk and ws onmessage can start mic
  useEffect(() => {
    startMicCaptureRef.current = startMicCapture;
  }, [startMicCapture]);

  // Keep guestLimitReachedRef in sync so callbacks (playNextChunk, ws onmessage) can block mic restart
  useEffect(() => {
    guestLimitReachedRef.current = guestLimitReached;
  }, [guestLimitReached]);

  const disconnectWs = useCallback(() => {
    stopMicCapture();
    stopPlayback();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
  }, [stopMicCapture, stopPlayback]);

  // ─── Panel open/close ─────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) {
      disconnectWs();
      releaseWakeLock();
      isPausedRef.current = false;
      setLanguage(null);
      setStatus('idle');
      setMicPermission('unknown');
      setErrorMsg('');
      setLastUserText('');
      setLastBuddyText('');
      setIntroDone(false);
      hasReceivedFirstAudioRef.current = false;
      guestTurnCountRef.current = 0;
      signInRequiredFiredRef.current = false;
      // Only clear guestLimitReached when closing; if they hit limit before, sessionStorage keeps them blocked
      const wasBlockedByStorage = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(GUEST_LIMIT_STORAGE_KEY) === 'true';
      if (!wasBlockedByStorage) setGuestLimitReached(false);
      return;
    }

    // When guest opens panel and they already used their 2 turns (any recipe), show sign-in and block speaking
    if (!isAuthenticated && typeof sessionStorage !== 'undefined' && sessionStorage.getItem(GUEST_LIMIT_STORAGE_KEY) === 'true') {
      setGuestLimitReached(true);
      if (onSignInRequired) onSignInRequired();
    }

    // Keep screen on while Frankie panel is open (e.g. phone won't sleep during conversation)
    requestWakeLock();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOpen, isAuthenticated, onSignInRequired, requestWakeLock, releaseWakeLock]);

  // Request mic and connect only after user has chosen a language
  useEffect(() => {
    if (!isOpen || language === null) return;

    // Request mic permission when panel is open and language is chosen
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicPermission('denied');
      setErrorMsg('Microphone is not available in this browser.');
      return;
    }

    setMicPermission('prompting');
    navigator.mediaDevices
      .getUserMedia({ audio: true, video: false })
      .then((stream) => {
        micStreamRef.current = stream;
        setMicPermission('granted');
        connectWs(language);
      })
      .catch(() => {
        setMicPermission('denied');
        setErrorMsg('Microphone access was denied. Allow it in browser settings and try again.');
      });
  }, [isOpen, language, connectWs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectWs();
      releaseWakeLock();
    };
  }, [disconnectWs, releaseWakeLock]);

  // ─── Mic button: tap to end turn when listening, or tap to resume when paused ─────────

  const handlePause = useCallback(() => {
    stopMicCapture();
    stopPlayback();
    isPausedRef.current = true;
    setStatus('paused');
  }, [stopMicCapture, stopPlayback]);

  const handleResume = useCallback(() => {
    isPausedRef.current = false;
    setStatus('listening');
    startMicCapture();
  }, [startMicCapture]);

  const handleMicButton = () => {
    if (status === 'paused') {
      handleResume();
    } else if (status === 'listening') {
      // Notify backend that user ended their turn (for guest limit enforcement)
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'turn_complete' }));
      }
      if (!isAuthenticated) {
        guestTurnCountRef.current += 1;
        if (guestTurnCountRef.current >= GUEST_TURN_LIMIT && !signInRequiredFiredRef.current) {
          signInRequiredFiredRef.current = true;
          // CRITICAL: set ref synchronously BEFORE any async work so
          // playNextChunk's setTimeout can't restart the mic after this point
          guestLimitReachedRef.current = true;
          setGuestLimitReached(true);
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(GUEST_LIMIT_STORAGE_KEY, 'true');
          }
          stopMicCapture();
          stopPlayback();
          setStatus('ready');
          // Delay popup slightly so it doesn't flash before state settles
          setTimeout(() => {
            if (onSignInRequired) onSignInRequired();
          }, 300);
          return; // exit early, don't set 'waiting_for_reply'
        }
      }
      stopMicCapture();
      setStatus('waiting_for_reply');
    }
  };

  // When initialOpen becomes true (e.g. navigated from home "Yes"), open the panel
  useEffect(() => {
    if (initialOpen && !isOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  if (!recipe || !recipeId) return null;

  const micDisabled = status === 'connecting' || status === 'idle' || status === 'error' || status === 'waiting_for_reply' || status === 'speaking' || micPermission !== 'granted' || guestLimitReached;
  const showPauseButton = (status === 'listening' || status === 'speaking' || status === 'waiting_for_reply') && micPermission === 'granted' && !guestLimitReached;

  const content = (
    <>
      {/* Floating Activate Button */}
      <button
        type="button"
        className="recipe-buddy-fab"
        onClick={() => setIsOpen((o) => !o)}
        aria-label={isOpen ? 'Close Recipe Buddy' : 'Activate Recipe Buddy voice assistant'}
      >
        {isOpen ? (
          <svg className="recipe-buddy-fab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <svg className="recipe-buddy-fab-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
          </svg>
        )}
        <span className="recipe-buddy-fab-label">{isOpen ? 'Close' : 'Speak To Frankie'}</span>
      </button>

      {/* Panel */}
      {isOpen && (
        <div className="recipe-buddy-panel" role="dialog" aria-label="Recipe Buddy voice assistant">
          {/* Step 1: Choose language before connecting, or "Sign in to continue" if guest already used 2 turns */}
          {language === null ? (
            guestLimitReached && !isAuthenticated ? (
              <div className="recipe-buddy-intro recipe-buddy-language-picker">
                <button
                  type="button"
                  className="recipe-buddy-intro-close"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                <p className="recipe-buddy-language-prompt">Sign in to continue talking to Frankie</p>
                <p className="recipe-buddy-notice recipe-buddy-notice--info" style={{ marginTop: '0.5rem', textAlign: 'center' }}>
                  You&apos;ve used your free turns. Sign in to keep chatting with Frankie on any recipe.
                </p>
              </div>
            ) : (
            <div className="recipe-buddy-intro recipe-buddy-language-picker">
              <button
                type="button"
                className="recipe-buddy-intro-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <p className="recipe-buddy-language-prompt">Choose your language / Kies jou taal</p>
              <div className="recipe-buddy-language-buttons">
                <button
                  type="button"
                  className="recipe-buddy-language-btn"
                  onClick={() => setLanguage('en')}
                  aria-label="Speak in English"
                >
                  English
                </button>
                <button
                  type="button"
                  className="recipe-buddy-language-btn"
                  onClick={() => setLanguage('af')}
                  aria-label="Praat in Afrikaans"
                >
                  Afrikaans
                </button>
              </div>
            </div>
            )
          ) : !introDone && micPermission !== 'denied' ? (
            <div className="recipe-buddy-intro">
              <button
                type="button"
                className="recipe-buddy-intro-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close"
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="recipe-buddy-intro-onion-wrap">
                <div className="recipe-buddy-intro-onion" aria-hidden>🧅</div>
              </div>
              <div className="recipe-buddy-intro-status-wrap">
                {(status === 'idle' || status === 'connecting' || status === 'ready') && (
                  <span className="recipe-buddy-intro-spinner" aria-hidden />
                )}
                <p className="recipe-buddy-intro-status">
                  {(status === 'idle' || status === 'connecting') && 'Connecting…'}
                  {status === 'ready' && 'Frankie is getting ready…'}
                  {(status === 'speaking' || status === 'listening' || status === 'waiting_for_reply') && 'Here we go!'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="recipe-buddy-panel-header">
                <div className="recipe-buddy-header-left">
                  <span className="recipe-buddy-nova-badge">Frankie</span>
                  <div>
                    <h3 className="recipe-buddy-panel-title">Recipe Buddy</h3>
                    <p className="recipe-buddy-panel-subtitle">{recipe.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="recipe-buddy-close-btn"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="recipe-buddy-body">
            {/* Permission / connection messages */}
            {micPermission === 'prompting' && (
              <p className="recipe-buddy-notice recipe-buddy-notice--info">
                Allow microphone access when your browser asks so you can speak to Recipe Buddy.
              </p>
            )}
            {micPermission === 'denied' && (
              <p className="recipe-buddy-notice recipe-buddy-notice--error">
                Microphone was blocked. Allow it in your browser settings and reload.
              </p>
            )}
            {status === 'connecting' && (
              <p className="recipe-buddy-notice recipe-buddy-notice--info">
                Connecting to Recipe Buddy…
              </p>
            )}
            {errorMsg && (
              <p className="recipe-buddy-notice recipe-buddy-notice--error" role="alert">
                {errorMsg}
              </p>
            )}
            {guestLimitReached && (
              <p className="recipe-buddy-notice recipe-buddy-notice--info" role="alert">
                Sign in to continue talking to Frankie.
              </p>
            )}

            {/* Pause: tap when you need to step away (e.g. chopping), then tap mic to continue */}
            {showPauseButton && (
              <button
                type="button"
                className="recipe-buddy-pause-btn"
                onClick={handlePause}
                aria-label="Pause conversation"
              >
                Pause
              </button>
            )}

            {/* Mic button: tap to end turn when listening, or tap to continue when paused */}
            <div className="recipe-buddy-mic-wrap">
                  <button
                    type="button"
                    className={[
                      'recipe-buddy-mic',
                      status === 'listening' ? 'recipe-buddy-mic--listening' : '',
                      status === 'listening' && isUserSpeaking ? 'recipe-buddy-mic--user-speaking' : '',
                      status === 'speaking' ? 'recipe-buddy-mic--speaking' : '',
                      status === 'waiting_for_reply' ? 'recipe-buddy-mic--waiting' : '',
                      status === 'paused' ? 'recipe-buddy-mic--paused' : '',
                      micDisabled && status !== 'paused' ? 'recipe-buddy-mic--disabled' : '',
                    ].join(' ')}
                    onClick={handleMicButton}
                    disabled={micDisabled && status !== 'paused'}
                    aria-label={status === 'paused' ? 'Tap to continue conversation' : status === 'listening' ? 'Tap when done speaking' : status === 'waiting_for_reply' ? 'Waiting for Frankie' : status === 'speaking' ? 'Frankie is speaking' : 'Microphone'}
                  >
                {status === 'listening' && <span className="recipe-buddy-pulse" aria-hidden />}
                {status === 'speaking' && <span className="recipe-buddy-wave" aria-hidden />}
                {status === 'paused' ? (
                  <svg className="recipe-buddy-mic-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                  </svg>
                ) : (
                  <svg className="recipe-buddy-mic-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                )}
              </button>

              <span className="recipe-buddy-status-label">
                {status === 'idle' && 'Starting…'}
                {status === 'connecting' && 'Connecting…'}
                {status === 'ready' && 'Starting…'}
                {status === 'listening' && (isUserSpeaking ? 'You\'re speaking…' : 'Listening…')}
                {status === 'waiting_for_reply' && 'Thinking…'}
                {status === 'speaking' && 'Frankie is speaking…'}
                {status === 'paused' && 'Paused — tap mic to continue'}
                {status === 'error' && 'Error — close and retry'}
              </span>
            </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(content, document.body);
  }
  return content;
}
